import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type { SurveyAssignmentDto } from '@/shared/api/types'
import { revokeAssignment } from './api'
import { count } from './vocab'

/**
 * Taking an assignment back.
 *
 * ## The assignment is kept, and that is the whole design
 *
 * A revoke sets the status and stores the reason. It does not delete the row and
 * it does not touch the attempts underneath it, because those attempts are the
 * fieldwork record: somebody knocked on those doors, and the refusals and
 * non-contacts they collected belong in the denominator whatever later happened
 * to the person who collected them.
 *
 * Deleting the assignment instead would remove those attempts from the eligible
 * base. The response rate would go UP — silently, with no entry anywhere saying
 * why — because the doors that did not answer would have stopped existing. That
 * is the single most attractive way to make a poll look better than it was, and
 * the reason the server has no DELETE here at all.
 *
 * ## Why the reason is mandatory
 *
 * `RevokeRequest.reason` is `@NotBlank` ≤500. Six months later the only surviving
 * explanation of why an interviewer's work stops mid-poll is this sentence, and
 * "reassigned" versus "back-check found fabrication" are different facts about
 * the data that remains.
 */
export function RevokeDialog({ open, surveyId, assignment, onClose, onSaved }: {
  open: boolean
  surveyId: string
  assignment: SurveyAssignmentDto
  onClose: () => void
  onSaved: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setReason('')
    setError(null)
  }, [open, assignment.id])

  const already = assignment.statusCode === 'REVOKED'

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await revokeAssignment(surveyId, assignment.id, reason.trim())
      onSaved()
    } catch (e) {
      // Two refusals worth reading verbatim: a blank reason comes back saying the
      // assignment is kept and why, and a second revoke comes back naming the
      // date of the first.
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="sm"
      title={`Revoke ${assignment.mobilizerName ?? 'this assignment'}`}
      description={assignment.scopeUnitName
        ? `Their assignment in ${assignment.scopeUnitName} stops here.`
        : 'Their assignment stops here.'}
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          {/*
            Not disabled on an empty reason. The server's refusal explains what a
            revocation does to the record, which is the thing somebody about to
            revoke should read; a greyed button teaches nothing.
          */}
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy} onClick={submit}>
            {busy ? 'Revoking…' : 'Revoke assignment'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        {already && (
          <Note tone="info">
            This one is already revoked
            {assignment.revokedAt ? ` — ${fmtDateTime(assignment.revokedAt)}` : ''}
            {assignment.revokeReason ? `: ${assignment.revokeReason}` : '.'}
          </Note>
        )}

        <Field label="Reason" required
          hint="Up to 500 characters. This is the only surviving explanation of why this work stopped.">
          <textarea className="erp-input w-full text-erp-sm" rows={3} maxLength={500}
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Moved to the Kisumu poll" />
        </Field>

        <Note tone="info">
          The assignment is <strong>kept</strong>, not deleted, and so are its attempts. They
          are the record of what was tried:
          {' '}{count(assignment.collected)} interview{assignment.collected === 1 ? '' : 's'}
          {' '}already collected under it stay in the poll, and every refusal and non-contact
          logged against it stays in the eligible base. Deleting it instead would shrink that
          denominator and quietly raise the poll's response rate — a better-looking number that
          nobody decided to publish.
        </Note>
      </div>
    </Modal>
  )
}
